import {
  Badge,
  Box,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  HStack,
  IconButton,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { useAuth, usePracticeAccess } from '@hb/react'
import {
  EDUCATION_CHECKLIST_GLOBAL_ITEMS,
  EDUCATION_CHECKLIST_ITEMS,
  EDUCATION_CHECKLIST_LOCKED_ITEMS,
  EncounterType,
  GaWindow,
  MIDWIVES,
  PrenatalExamData,
  USERS_ADMIN,
  WithId,
} from '@hb/shared'
import { collection, CollectionReference, doc, query, setDoc, where } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import { db } from '../../../../backend/db/db'
import { useEducationConfig } from '../../../../hooks/backend/education/useEducationConfig'
import { useQuery } from '../../../../hooks/backend/useQuery'
import { useAssessmentExams } from '../../../../hooks/backend/user/useAssessmentExams'
import { SectionLabel } from '../../SectionLabel'

// ---------------------------------------------------------------------------
// Firestore document shape for checklist items (locked, global, practice, personal)
// ---------------------------------------------------------------------------
interface ChecklistItemDoc {
  text: string
  gaWindow: GaWindow
  sortOrder: number
  createdOn: number
}

// ---------------------------------------------------------------------------
// Per-item data stored in the encounter
// ---------------------------------------------------------------------------
type EncounterChecklistItem = NonNullable<
  PrenatalExamData['patientEducationChecklist']
>['items'][number]

// ---------------------------------------------------------------------------
// Lock icon (inline SVG — avoids a dependency)
// ---------------------------------------------------------------------------
const LockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }}>
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
)

// ---------------------------------------------------------------------------
// Derive GA window from encounter type and GA in days
// ---------------------------------------------------------------------------
function getGaWindow(
  examType: EncounterType,
  gaInDays: number | undefined,
  config: { firstTrimesterEndDays: number; secondTrimesterEndDays: number },
): GaWindow {
  if (examType === EncounterType.POSTPARTUM) return 'postpartum'
  if (typeof gaInDays !== 'number') return 'first-trimester'
  if (gaInDays <= config.firstTrimesterEndDays) return 'first-trimester'
  if (gaInDays <= config.secondTrimesterEndDays) return 'second-trimester'
  return 'third-trimester'
}

// ---------------------------------------------------------------------------
// Single item row
// ---------------------------------------------------------------------------
const ItemRow = ({
  item,
  isLocked,
  onToggle,
  onNoteChange,
  onRemove,
  softPrompt,
}: {
  item: EncounterChecklistItem
  isLocked: boolean
  onToggle: (itemId: string, checked: boolean) => void
  onNoteChange: (itemId: string, note: string) => void
  onRemove?: (itemId: string) => void
  softPrompt?: string
}) => (
  <Box py={2} borderBottomWidth="1px" borderColor="gray.100" _dark={{ borderColor: 'gray.700' }}>
    {softPrompt && (
      <Text fontSize="xs" color="orange.600" mb={1} fontStyle="italic">
        {softPrompt}
      </Text>
    )}
    <Flex align="flex-start" gap={2}>
      <Checkbox
        mt={0.5}
        isChecked={item.checked}
        onChange={e => onToggle(item.itemId, e.target.checked)}
      />
      <Box flex={1}>
        <HStack spacing={1} flexWrap="wrap">
          {isLocked && (
            <Badge colorScheme="purple" fontSize="10px" mr={1}>
              <LockIcon />
              Required
            </Badge>
          )}
          <Text fontSize="sm">{item.itemText}</Text>
        </HStack>
        {item.checked && (
          <Textarea
            mt={1}
            size="xs"
            placeholder="Optional note…"
            value={item.note ?? ''}
            onChange={e => onNoteChange(item.itemId, e.target.value)}
            rows={2}
          />
        )}
      </Box>
      {!isLocked && onRemove && (
        <IconButton
          aria-label="Remove item"
          icon={<span style={{ fontSize: 14 }}>×</span>}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          onClick={() => onRemove(item.itemId)}
        />
      )}
    </Flex>
  </Box>
)

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const PatientEducationChecklistInput = ({
  assessmentId,
  examType,
}: {
  assessmentId: string | null
  examType: EncounterType
}) => {
  const config = useEducationConfig()
  const { selectedPracticeId } = usePracticeAccess()
  const currentUserId = useAuth(s => s.authUser?.uid)

  const {
    input: { value: gaInDays },
  } = useField<number | undefined>('data.gestationalAge', { subscription: { value: true } })

  const {
    input: { value: checklist, onChange: setChecklist },
  } = useField<PrenatalExamData['patientEducationChecklist']>('data.patientEducationChecklist', {
    subscription: { value: true },
  })

  const gaWindow = useMemo(
    () => getGaWindow(examType, gaInDays, config),
    [examType, gaInDays, config],
  )

  // ---- Firestore queries for checklist items --------------------------------
  const lockedQuery = useMemo(
    () =>
      query(
        collection(db, EDUCATION_CHECKLIST_LOCKED_ITEMS) as CollectionReference<ChecklistItemDoc>,
        where('gaWindow', '==', gaWindow),
      ),
    [gaWindow],
  )
  const globalQuery = useMemo(
    () =>
      query(
        collection(db, EDUCATION_CHECKLIST_GLOBAL_ITEMS) as CollectionReference<ChecklistItemDoc>,
        where('gaWindow', '==', gaWindow),
      ),
    [gaWindow],
  )
  const practiceQuery = useMemo(() => {
    if (!selectedPracticeId) return null
    return query(
      collection(
        db,
        MIDWIVES,
        selectedPracticeId,
        EDUCATION_CHECKLIST_ITEMS,
      ) as CollectionReference<ChecklistItemDoc>,
      where('gaWindow', '==', gaWindow),
    )
  }, [selectedPracticeId, gaWindow])
  const personalQuery = useMemo(() => {
    if (!currentUserId) return null
    return query(
      collection(
        db,
        USERS_ADMIN,
        currentUserId,
        EDUCATION_CHECKLIST_ITEMS,
      ) as CollectionReference<ChecklistItemDoc>,
      where('gaWindow', '==', gaWindow),
    )
  }, [currentUserId, gaWindow])

  const { dataArr: lockedItems } = useQuery<ChecklistItemDoc>(lockedQuery)
  const { dataArr: globalItems } = useQuery<ChecklistItemDoc>(globalQuery)
  const { dataArr: practiceItems } = useQuery<ChecklistItemDoc>(practiceQuery)
  const { dataArr: personalItems } = useQuery<ChecklistItemDoc>(personalQuery)

  // ---- Prior encounters for soft prompt ------------------------------------
  const { dataArr: priorEncounters } = useAssessmentExams(assessmentId, EncounterType.ANTEPARTUM)

  // ---- Soft prompt computation ---------------------------------------------
  const softPromptMap = useMemo<Record<string, string>>(() => {
    if (!lockedItems || !priorEncounters) return {}
    const map: Record<string, string> = {}
    for (const item of lockedItems) {
      const sameWindow = priorEncounters.filter(enc => {
        const cl = (enc.data as PrenatalExamData | undefined)?.patientEducationChecklist
        return cl?.gaWindow === gaWindow
      })
      if (sameWindow.length < 2) continue
      let consecutive = 0
      for (const enc of sameWindow) {
        const items =
          (enc.data as PrenatalExamData | undefined)?.patientEducationChecklist?.items ?? []
        const entry = items.find(i => i.itemId === item.id)
        if (!entry?.checked) consecutive++
        else break
      }
      if (consecutive >= 2) {
        map[item.id] =
          `"${item.text}" has not been documented in ${consecutive} visits — consider addressing today.`
      }
    }
    return map
  }, [lockedItems, priorEncounters, gaWindow])

  const hasSoftPrompts = Object.keys(softPromptMap).length > 0

  // ---- Auto-populate on mount (only when checklist absent) -----------------
  useEffect(() => {
    if (checklist) return
    const allReady =
      lockedItems !== null &&
      globalItems !== null &&
      practiceItems !== null &&
      personalItems !== null
    if (!allReady) return

    // Build exclusion set from personal items marked excluded: true.
    // These are items the provider removed on a prior visit — skip them in all source lists.
    const excludedIds = new Set(
      (personalItems ?? []).filter(i => (i as any).excluded === true).map(i => i.id),
    )

    const toItems = (
      arr: WithId<ChecklistItemDoc>[],
      source: EncounterChecklistItem['source'],
    ): EncounterChecklistItem[] =>
      arr
        .filter(doc => !(doc as any).excluded && !excludedIds.has(doc.id))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map(doc => ({
          itemId: doc.id,
          itemText: doc.text,
          source,
          checked: false,
        }))

    setChecklist({
      gaWindow,
      items: [
        ...toItems(lockedItems, 'locked'),
        ...toItems(globalItems, 'global'),
        ...toItems(practiceItems, 'practice'),
        ...toItems(personalItems, 'personal'),
      ],
    })
  }, [checklist, lockedItems, globalItems, practiceItems, personalItems, gaWindow, setChecklist])

  // ---- Collapsible state ---------------------------------------------------
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    if (hasSoftPrompts) setIsOpen(true)
  }, [hasSoftPrompts])

  // ---- Item mutation helpers -----------------------------------------------
  const updateItem = useCallback(
    (itemId: string, patch: Partial<EncounterChecklistItem>) => {
      if (!checklist) return
      setChecklist({
        ...checklist,
        items: checklist.items.map(i =>
          i.itemId === itemId
            ? {
                ...i,
                ...patch,
                ...(patch.checked ? { checkedAt: Date.now() } : { checkedAt: undefined }),
              }
            : i,
        ),
      })
    },
    [checklist, setChecklist],
  )

  const handleToggle = useCallback(
    (itemId: string, checked: boolean) => updateItem(itemId, { checked }),
    [updateItem],
  )

  const handleNoteChange = useCallback(
    (itemId: string, note: string) => updateItem(itemId, { note }),
    [updateItem],
  )

  const handleRemove = useCallback(
    async (itemId: string) => {
      if (!checklist) return
      // Remove from encounter snapshot
      setChecklist({
        ...checklist,
        items: checklist.items.filter(i => i.itemId !== itemId),
      })
      // Persist exclusion to provider's personal subcollection — include gaWindow so the
      // personalQuery (which filters by gaWindow) can return it and the auto-populate can
      // skip this item on future visits.
      if (!currentUserId) return
      const exclusionRef = doc(db, USERS_ADMIN, currentUserId, EDUCATION_CHECKLIST_ITEMS, itemId)
      await setDoc(
        exclusionRef,
        { excluded: true, excludedAt: Date.now(), gaWindow, text: '', sortOrder: 0, createdOn: 0 },
        { merge: true },
      )
    },
    [checklist, setChecklist, currentUserId, gaWindow],
  )

  const lockedIds = useMemo(() => new Set(lockedItems?.map(i => i.id) ?? []), [lockedItems])

  if (!checklist) return null

  return (
    <Box px={4} py={3} w="100%" data-child-name="data.patientEducationChecklist">
      <Flex
        align="center"
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsOpen(o => !o)}>
        <SectionLabel>Patient Education Checklist</SectionLabel>
        <HStack spacing={2}>
          {hasSoftPrompts && (
            <Badge colorScheme="orange" fontSize="10px">
              {Object.keys(softPromptMap).length} follow-up
              {Object.keys(softPromptMap).length > 1 ? 's' : ''}
            </Badge>
          )}
          <Text fontSize="xs" color="gray.500">
            {isOpen ? '▲' : '▼'}
          </Text>
        </HStack>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Divider />
        <Box mt={2}>
          {checklist.items.length === 0 && (
            <Text fontSize="sm" color="gray.500" py={1}>
              No education items for this visit.
            </Text>
          )}
          {checklist.items.map(item => (
            <ItemRow
              key={item.itemId}
              item={item}
              isLocked={lockedIds.has(item.itemId)}
              onToggle={handleToggle}
              onNoteChange={handleNoteChange}
              onRemove={lockedIds.has(item.itemId) ? undefined : handleRemove}
              softPrompt={softPromptMap[item.itemId]}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}
